import { Link, useParams } from "react-router-dom";
import {
  useGetToolProfileQuery,
  useUpdateToolProfileMutation,
} from "../features/model/toolprofileSlice";
import {
  Button,
  ButtonGroup,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Stack,
  TextField,
  FormLabel,
  FormGroup,
  Grid,
  Box,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Typography,
} from "@mui/material";
import Loading from "../components/Loading";
import { useCallback, useState, useEffect } from "react";
import DropArea from "../components/DropArea";
import { ProfilePreviewImage } from "./po/Extras";
import { useRegenerateToolPreviewMutation } from "../features/templates/fetchToolPreview";
import { encodeBase64 } from "../helpers/images";
import { Preview3D } from "../components/Preview3D";

const useDebounce = (profile) => {
  const [debouncedProfile, setDebouncedProfile] = useState(profile);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedProfile(profile);
    }, 1500);

    return () => {
      clearTimeout(handler);
    };
  }, [profile]);

  return debouncedProfile;
};

const ToolProfileEditView = () => {
  let params = useParams();

  let default_state = {
    width: 900,
    outline: true,
    tool: {
      id: 0,
      name: "",
      usage: {
        inner: {
          enabled: false,
          depth: 0.0,
          offset: 0.0,
          corners: "",
          template_name: "",
        },
        outer: {
          enabled: false,
          depth: 0.0,
          offset: 0.0,
          corners: "",
          template_name: "",
        },
        raised: {
          enabled: false,
          depth: 0.0,
          offset: 0.0,
          corners: "",
          template_name: "",
        },
      },
      profile: "",
      svg: "",
      icon: "",
      aliases: "",
      main: false,
    },
  };

  let [state, setState] = useState(default_state);
  let [fileSizeDialogState, setFileSizeDialogState] = useState(false);
  let [fileTypeDialogState, setTypeDialogState] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);

  const debouncedProfile = useDebounce(state);

  useEffect(() => {
    if (!debouncedProfile || !debouncedProfile.tool.svg) {
      console.log("No SVG to fetch preview for");
      return;
    }

    refreshPreview();

    fetch(process.env.REACT_APP_API_URL + "v1/tool/preview", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(debouncedProfile),
    })
      .then((response) => response.blob())
      .then((blob) => {
        const reader = new FileReader();
        reader.onload = () => {
          const img = reader.result;
          setPreviewImage(img);
        };
        reader.readAsDataURL(blob);
      })
      .catch((error) => {
        console.error("Error fetching preview:", error);
      });
  }, [debouncedProfile]);

  const {
    data: toolProfile,
    error,
    isLoading,
    refetch: refetchProfile
  } = useGetToolProfileQuery(params.objectId);
  const [updateToolProfile, { isLoadingUpdate: isUpdating }] =
    useUpdateToolProfileMutation();
  const [regenerateToolPreview] = useRegenerateToolPreviewMutation();

  const [refreshCounter, setRefreshCounter] = useState(0);
  const [previewRefreshCounter, setPreviewRefreshCounter] = useState(0);

  function refresh() {
    setRefreshCounter(refreshCounter + 1);
  }
  function refreshPreview() {
    setPreviewRefreshCounter(previewRefreshCounter + 1);
  }

  const uploadPNG = useCallback((files) => {
    let file = files[0];
    const reader = new FileReader();

    reader.onabort = () => console.log("file reading was aborted");
    reader.onerror = () => console.log("file reading has failed");
    reader.onload = () => {
      const data = reader.result;
      updateToolProfile({toolprofile_id: state.tool.id, data: {...state.tool, icon: data, usage: JSON.stringify(state.tool.usage)}});
      setState({...state, tool: {...state.tool, icon: data}})
      refresh();
    };
    reader.readAsDataURL(file);
  });

  const uploadSVG = useCallback((files) => {
    let file = files[0];
    if (file.size > 10000) {
      setFileSizeDialogState(true);
      return;
    }
    const reader = new FileReader();
    reader.onabort = () => console.log("file reading was aborted");
    reader.onerror = () => console.log("file reading has failed");
    reader.onload = () => {
      const fileData = reader.result;
      if (
        fileData.startsWith('<?xml version="1.0"') ||
        fileData.startsWith("<svg ")
      ) {
        updateToolProfile({toolprofile_id: state.tool.id, data: {...state.tool, svg: fileData, usage: JSON.stringify(state.tool.usage)}})
        setState({...state, tool: {...state.tool, svg: fileData}})
      } else {
        setTypeDialogState(true);
      }
    };
    reader.readAsText(file);
  });

  if (isLoading) {
    return <Loading />;
  }
  if (state.tool.id !== toolProfile.id) {
    setState((prev) => {
      return {
        ...prev,
        tool: {
          ...prev.tool,
          id: toolProfile.id,
          name: toolProfile.name,
          svg: toolProfile.svg,
          icon: toolProfile.icon,
          main: toolProfile.main,
          profile: toolProfile.profile,
          aliases: toolProfile.aliases,
          usage: JSON.parse(toolProfile.usage)
        },
      };
    });
  }

  const handleRevert = () => {
    setState(toolProfile);
  };

  const handleSave = () => {
    var toolDict = {...state.tool, usage: JSON.stringify(state.tool.usage)};
    updateToolProfile({
      toolprofile_id: state.tool.id,
      data: toolDict,
    });
  };

  const handleCheckboxChange = (event) => {
    if (event.target.checked) {
      setState({ ...state, tool: {...state.tool, main: true }});
    } else {
      setState({ ...state, tool: {...state.tool, main: false }});
    }
  };
  const handleNameChange = (event) => {
    setState({ ...state, tool: {...state.tool, name: event.target.value }});
  };
  const handleAliasesChange = (event) => {
    setState({ ...state, tool: {...state.tool, aliases: event.target.value }});
  };

  const UsageField = {
    ENABLED: "enabled",
    DEPTH: "depth",
    OFFSET: "offset",
    CORNERS: "corners",
    TOOL_PATH_TEMPLATE_NAME: "template_name",
  };

  const CornersField = {
    NONE: "None",
    VBIT: "Vbit",
    CALCULATED: "Calculated",
  };

  const handleUsageChange = (type, field) => (event) => {
    let value =
      field === UsageField.ENABLED ? event.target.checked : event.target.value;
    value = field === UsageField.DEPTH || field === UsageField.OFFSET ? parseFloat(value) : value;
    setState((prev) => {
      return {
        ...prev,
        tool: {
          ...prev.tool,
          usage: {
            ...prev.tool.usage,
            [type]: {
              ...prev.tool.usage[type],
              [field]: value,
            },
          },
        },
      };
    });
  };

  const handleFileTooBigClose = () => {
    setFileSizeDialogState(false);
  };

  const handleWrongFileTypeClose = () => {
    setTypeDialogState(false);
  };

  const icon_preview = <img style={{alignSelf: "center"}} src={`${state.tool.icon}`} width={32} height={32} alt="icon" />;

  return (
    <Stack sx={{ marginTop: 2, marginBottom: 4 }} spacing={2}>
      <ButtonGroup>
        <Button component={Link} to={`/toolprofiles`}>
          Back
        </Button>
      </ButtonGroup>
      <TextField
        label="name"
        key="name"
        value={state.tool.name}
        onChange={handleNameChange}
      />
      <TextField
        label="aliases"
        key="aliases"
        value={state.tool.aliases}
        onChange={handleAliasesChange}
      />
      <Stack direction="row" spacing={2} key="svg">
        <Paper sx={{padding: 1}}>
        <Stack direction="column" spacing={1}>
          <Typography variant="caption">Profile SVG</Typography>
          <DropArea fileHandler={uploadSVG} label={"Upload Profile"} />
          <img key="image" src={`data:image/svg+xml;utf8,${encodeURIComponent(state.tool.svg)}`} alt="Preview" style={{ borderRadius: 4 }}/>
        </Stack>
        </Paper>
        {
          previewImage && (
            <img
              key="preview-image"
              src={previewImage}
              alt="Preview"
              style={{ borderRadius: 4, width: 900, height: 300, alignSelf: "center" }}
            />
          )
        }
        {
          state.tool.svg && <Preview3D data={debouncedProfile} counter={previewRefreshCounter} sx={{width: 300, height: 300}}/>
        }
        <Dialog
          open={fileSizeDialogState}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">{"File is too big"}</DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              The file being uploaded it too big. Please make sure the file is
              an appropriately sized svg.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleFileTooBigClose} autoFocus>
              Ok
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={fileTypeDialogState}
          aria-labelledby="file-type-alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="file-type- alert-dialog-title">
            {"Wrong file type"}
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="file-type-alert-dialog-description">
              The file uploaded does not appear to be an SVG. Please try again
              with a properly formatted SVG file.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleWrongFileTypeClose} autoFocus>
              Ok
            </Button>
          </DialogActions>
        </Dialog>
      </Stack>
      <Paper sx={{padding: 1}}>
        <Stack direction="column" spacing={1}>
          <Typography variant="caption">Icon</Typography>
      <Stack direction="row" spacing={2} alignItems={"stretch"}>
        {icon_preview}
        <DropArea fileHandler={uploadPNG} label="Upload Icon" />
        <Button onClick={() => {
            regenerateToolPreview({id: state.tool.id})
            refetchProfile();
        }} variant="outlined">
           Generate from profile</Button>
      </Stack>
      </Stack>
      </Paper>
      <Stack direction="row" spacing={2} key="usage" sx={{ width: "100%" }}>
        <FormControl fullWidth>
          <FormLabel component="legend">Usage</FormLabel>
          <FormGroup>
            {Object.keys(state.tool.usage).map((type) => (
              <Box key={type} mb={2}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={2}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={state.tool.usage[type].enabled}
                          onChange={handleUsageChange(type, UsageField.ENABLED)}
                          name={type}
                        />
                      }
                      label={type.charAt(0).toUpperCase() + type.slice(1)}
                      style={{ marginRight: 0 }}
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <TextField
                      fullWidth
                      label="Depth"
                      defaultValue={state.tool.usage[type].depth}
                      onChange={handleUsageChange(type, UsageField.DEPTH)}
                      disabled={!state.tool.usage[type].enabled}
                      type="number"
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <TextField
                      fullWidth
                      label="Offset"
                      defaultValue={state.tool.usage[type].offset}
                      onChange={handleUsageChange(type, UsageField.OFFSET)}
                      disabled={!state.tool.usage[type].enabled}
                      type="number"
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <FormControl fullWidth>
                      <InputLabel id="corners-select-label">Corners</InputLabel>
                      <Select
                        labelId="corners-select-label"
                        defaultValue={state.tool.usage[type].corners}
                        onChange={(event) => {
                          setState((prev) => {
                            return {
                              ...prev,
                              tool: {
                                ...prev.tool,
                                usage: {
                                  ...prev.tool.usage,
                                  [type]: {
                                    ...prev.tool.usage[type],
                                    corners: event.target.value,
                                  },
                                },
                              },
                            };
                          });
                        }}
                        disabled={!state.tool.usage[type].enabled}
                      >
                        {Object.values(CornersField).map((corner, index) => (
                          <MenuItem key={index} value={corner}>
                            {corner}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={2}>
                    <TextField
                      fullWidth
                      label="Toolpath Template Name"
                      value={state.tool.usage[type].template_name}
                      onChange={handleUsageChange(
                        type,
                        UsageField.TOOL_PATH_TEMPLATE_NAME
                      )}
                      disabled={!state.tool.usage[type].enabled}
                    />
                  </Grid>
                </Grid>
              </Box>
            ))}
          </FormGroup>
        </FormControl>
      </Stack>
      <FormControlLabel
        control={
          <Checkbox checked={state.main} onChange={handleCheckboxChange} />
        }
        label="In the main carousel"
      />

      <ButtonGroup>
        <Button onClick={handleRevert}>Revert</Button>
        <Button variant="contained" onClick={handleSave}>
          Save
        </Button>
      </ButtonGroup>
    </Stack>
  );
};

export default ToolProfileEditView;
